// The scripted conversation with Neo on the home page's iPad (TOM-980).
//
// The script is turned into a timeline of frames once, at module load. Each
// frame is everything the chat panel shows plus how long it stays. The panel
// component only steps through the frames, so it can pause and resume at any
// point, and these rules run under node with no DOM.
import { SCENE_READINGS, anchorAnswer } from './homeScene';
import type { SceneHeadline } from './sceneStatus';

export interface ChatLine {
  from: 'user' | 'neo';
  text: string;
}

export const CHAT_SCRIPT: readonly ChatLine[] = [
  { from: 'user', text: 'Hey Neo, how much fresh water do we have left?' },
  {
    from: 'neo',
    text: `The tank is at ${SCENE_READINGS.waterPct}%, about ${SCENE_READINGS.waterGal} of its ${SCENE_READINGS.waterCapacityGal} gallons.`,
  },
  { from: 'user', text: 'Is the anchor holding?' },
  { from: 'neo', text: anchorAnswer() },
];

/** The approved design's timings, in milliseconds. */
export const CHAT_PACING = {
  /** The panel stays closed before it slides in. */
  closedMs: 2800,
  /** The panel slides in before the first question is typed. */
  openingMs: 900,
  /** Each character of a question. */
  keyMs: 34,
  /** A finished question waits in the box before it is sent. */
  beforeSendMs: 450,
  /** After a question is sent. */
  afterSendMs: 500,
  /** Neo's typing dots before its reply. */
  thinkingMs: 1300,
  /** Neo's reply appears this many characters at a time... */
  replyStep: 2,
  /** ...at this interval. */
  replyMs: 22,
  /** After a reply is complete. */
  afterReplyMs: 1600,
  /** The finished conversation stays this much longer. */
  endMs: 3000,
  /** The panel slides out before the loop starts again. */
  closingMs: 900,
};

export type ChatPacing = typeof CHAT_PACING;

export interface ChatFrame {
  /** Whether the panel has slid in. */
  open: boolean;
  /** How many script lines are complete. */
  shown: number;
  /** The question being typed into the box. */
  draft: string;
  /** Neo's typing dots. */
  thinking: boolean;
  /** Neo's reply so far, or null. */
  typing: string | null;
  /** How long the frame stays, in milliseconds. */
  holdMs: number;
}

const BASE: Omit<ChatFrame, 'holdMs'> = {
  open: false,
  shown: 0,
  draft: '',
  thinking: false,
  typing: null,
};

/** Every frame of one loop of the conversation, in order. The last leads back to the first. */
export function buildChatTimeline(
  script: readonly ChatLine[],
  pacing: ChatPacing = CHAT_PACING
): ChatFrame[] {
  const frames: ChatFrame[] = [{ ...BASE, holdMs: pacing.closedMs }];
  frames.push({ ...BASE, open: true, holdMs: pacing.openingMs });
  let shown = 0;
  for (const line of script) {
    const at = { ...BASE, open: true, shown };
    if (line.from === 'user') {
      for (let i = 1; i <= line.text.length; i++) {
        const last = i === line.text.length;
        frames.push({
          ...at,
          draft: line.text.slice(0, i),
          holdMs: pacing.keyMs + (last ? pacing.beforeSendMs : 0),
        });
      }
      shown += 1;
      frames.push({ ...BASE, open: true, shown, holdMs: pacing.afterSendMs });
    } else {
      frames.push({ ...at, thinking: true, holdMs: pacing.thinkingMs });
      for (let i = 1; i <= line.text.length; i += pacing.replyStep) {
        frames.push({ ...at, typing: line.text.slice(0, i), holdMs: pacing.replyMs });
      }
      shown += 1;
      frames.push({ ...BASE, open: true, shown, holdMs: pacing.afterReplyMs });
    }
  }
  const last = frames[frames.length - 1]!;
  last.holdMs += pacing.endMs;
  frames.push({ ...BASE, shown, holdMs: pacing.closingMs });
  return frames;
}

/** The finished conversation with the panel open, shown under reduced motion. */
export function finishedChatFrame(script: readonly ChatLine[]): ChatFrame {
  return { ...BASE, open: true, shown: script.length, holdMs: 0 };
}

export const CHAT_TIMELINE = buildChatTimeline(CHAT_SCRIPT);
export const CHAT_FINISHED = finishedChatFrame(CHAT_SCRIPT);

/** The line under Neo's name in the panel's header. */
export function chatStatus(frame: ChatFrame, headline: SceneHeadline): string {
  return frame.thinking || frame.typing !== null ? 'Neo is typing…' : headline.text;
}

/** Whether the loop plays: the hero is on screen, the tab is visible, and motion is allowed. */
export function chatRuns(when: {
  onScreen: boolean;
  tabHidden: boolean;
  reducedMotion: boolean;
}): boolean {
  return when.onScreen && !when.tabHidden && !when.reducedMotion;
}

/** The frame after `index`, wrapping to the start. */
export function nextFrameIndex(index: number, length: number): number {
  return (index + 1) % length;
}
